import { useEffect, lazy, Suspense } from "react";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import TidioIdentify from "@/components/TidioIdentify";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nContext";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useThemeStore } from "@/store/themeStore";
import LangLayout, { GeoRedirect, LegacyRedirect } from "@/components/LangLayout";

// Critical path — only tiny pages loaded eagerly
import MaintenancePage from "@/pages/MaintenancePage";
import NotFound from "./pages/NotFound";

// Index is heavy — lazy load it
// Retry wrapper for lazy imports – handles stale chunk errors after redeploy
function lazyRetry(factory: () => Promise<any>) {
  return lazy(() =>
    factory().catch((err) => {
      // If the chunk fetch failed, do a hard reload once
      const key = "chunk-retry";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves – page is reloading
      }
      sessionStorage.removeItem(key);
      throw err;
    })
  );
}

const Index = lazyRetry(() => import("./pages/Index"));

// Lazy-loaded pages — split into separate chunks
const CartPage = lazyRetry(() => import("./pages/CartPage"));
const CheckoutPage = lazyRetry(() => import("./pages/CheckoutPage"));
const FAQPage = lazyRetry(() => import("./pages/FAQPage"));
const ContactPage = lazyRetry(() => import("./pages/ContactPage"));
const QuickPaymentPage = lazyRetry(() => import("./pages/QuickPaymentPage"));
const PaymentStatusPage = lazyRetry(() => import("./pages/PaymentStatusPage"));
const BlogPage = lazyRetry(() => import("./pages/BlogPage"));
const BlogPostPage = lazyRetry(() => import("./pages/BlogPostPage"));
const FoilsPage = lazyRetry(() => import("./pages/FoilsPage"));
const RegulaminPage = lazyRetry(() => import("./pages/RegulaminPage"));
const PrivacyPolicyPage = lazyRetry(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazyRetry(() => import("./pages/CookiePolicyPage"));
const WithdrawalPage = lazyRetry(() => import("./pages/WithdrawalPage"));
const FileReceiverPage = lazyRetry(() => import("./pages/FileReceiverPage"));
const AuthPage = lazyRetry(() => import("./pages/AuthPage"));
const AccountPage = lazyRetry(() => import("./pages/AccountPage"));
const ResetPasswordPage = lazyRetry(() => import("./pages/ResetPasswordPage"));
const AboutPage = lazyRetry(() => import("./pages/AboutPage"));
const PortfolioPage = lazyRetry(() => import("./pages/PortfolioPage"));
const OAuthConsentPage = lazyRetry(() => import("./pages/OAuthConsent"));
const SheetsConfiguratorPage = lazyRetry(() => import("./pages/SheetsConfiguratorPage"));

const AdminLoginPage = lazyRetry(() => import("./pages/admin/AdminLoginPage"));
const AdminLayout = lazyRetry(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazyRetry(() => import("./pages/admin/AdminDashboard"));
const AdminBlogPage = lazyRetry(() => import("./pages/admin/AdminBlogPage"));
const AdminOrdersPage = lazyRetry(() => import("./pages/admin/AdminOrdersPage"));
const AdminPricesPage = lazyRetry(() => import("./pages/admin/AdminPricesPage"));
const AdminMessagesPage = lazyRetry(() => import("./pages/admin/AdminMessagesPage"));
const AdminShippingPage = lazyRetry(() => import("./pages/admin/AdminShippingPage"));
const AdminGalleryPage = lazyRetry(() => import("./pages/admin/AdminGalleryPage"));
const AdminFAQPage = lazyRetry(() => import("./pages/admin/AdminFAQPage"));
const AdminChatsPage = lazyRetry(() => import("./pages/admin/AdminChatsPage"));
const AdminDiscountsPage = lazyRetry(() => import("./pages/admin/AdminDiscountsPage"));
const AdminInspirationsPage = lazyRetry(() => import("./pages/admin/AdminInspirationsPage"));
const AdminReviewsPage = lazyRetry(() => import("./pages/admin/AdminReviewsPage"));
const AdminLoyaltyPage = lazyRetry(() => import("./pages/admin/AdminLoyaltyPage"));
const AdminCustomersPage = lazyRetry(() => import("./pages/admin/AdminCustomersPage"));
const AdminTrustedDevicesPage = lazyRetry(() => import("./pages/admin/AdminTrustedDevicesPage"));
const AdminErrorLogsPage = lazyRetry(() => import("./pages/admin/AdminErrorLogsPage"));
const AdminSupportChatPage = lazyRetry(() => import("./pages/admin/AdminSupportChatPage"));
const AdminSettingsPage = lazyRetry(() => import("./pages/admin/AdminSettingsPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Ładowanie...</span>
    </div>
  </div>
);

function AppRoutes() {
  const { isMaintenanceMode, isLoading } = useMaintenanceMode();

  const isLovablePreview = window.location.hostname.includes('lovableproject.com')
    || window.location.hostname === 'localhost';

  const hasLocalBypass = localStorage.getItem('bypass_maintenance') === 'true';
  const showMaintenance = !isLoading && isMaintenanceMode && !isLovablePreview && !hasLocalBypass;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* OAuth consent for external MCP clients (ChatGPT, Claude, Cursor, ...) */}
        <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />
        {/* Admin routes – no lang prefix */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="blog" element={<AdminBlogPage />} />
          <Route path="orders" element={<AdminOrdersPage />} />
          <Route path="prices" element={<AdminPricesPage />} />
          <Route path="shipping" element={<AdminShippingPage />} />
          <Route path="gallery" element={<AdminGalleryPage />} />
          <Route path="faq" element={<AdminFAQPage />} />
          <Route path="messages" element={<AdminMessagesPage />} />
          <Route path="chats" element={<AdminChatsPage />} />
          <Route path="support-chat" element={<AdminSupportChatPage />} />
          <Route path="discounts" element={<AdminDiscountsPage />} />
          <Route path="inspirations" element={<AdminInspirationsPage />} />
          <Route path="reviews" element={<AdminReviewsPage />} />
          <Route path="loyalty" element={<AdminLoyaltyPage />} />
          <Route path="customers" element={<AdminCustomersPage />} />
          <Route path="trusted-devices" element={<AdminTrustedDevicesPage />} />
          <Route path="error-logs" element={<AdminErrorLogsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>

        {/* Root "/" → geo-detect and redirect to /:lang/ */}
        <Route path="/" element={<GeoRedirect />} />

        {/* Language-prefixed public routes */}
        <Route path="/:lang" element={<LangLayout />}>
          {showMaintenance ? (
            <Route path="*" element={<MaintenancePage />} />
          ) : (
            <>
              <Route index element={<Index />} />
              <Route path="kreator" element={<Index />} />
              <Route path="kreator/arkusze" element={<SheetsConfiguratorPage />} />
              <Route path="koszyk" element={<CartPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="faq" element={<FAQPage />} />
              <Route path="kontakt" element={<ContactPage />} />
              <Route path="o-nas" element={<AboutPage />} />
              <Route path="szybka-platnosc" element={<QuickPaymentPage />} />
              <Route path="platnosc-status" element={<PaymentStatusPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="blog/:slug" element={<BlogPostPage />} />
              <Route path="folie-i-laminaty" element={<FoilsPage />} />
              <Route path="foils-and-laminates" element={<FoilsPage />} />
              <Route path="regulamin" element={<RegulaminPage />} />
              <Route path="polityka-prywatnosci" element={<PrivacyPolicyPage />} />
              <Route path="polityka-cookies" element={<CookiePolicyPage />} />
              <Route path="odstapienie" element={<WithdrawalPage />} />
              <Route path="receive-file" element={<FileReceiverPage />} />
              <Route path="logowanie" element={<AuthPage />} />
              <Route path="login" element={<AuthPage />} />
              <Route path="konto" element={<AccountPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="reset-haslo" element={<ResetPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="portfolio" element={<PortfolioPage />} />
              <Route path="realizacje" element={<PortfolioPage />} />
              <Route path="quick-payment" element={<QuickPaymentPage />} />
              <Route path="cart" element={<CartPage />} />
              <Route path="payment-status" element={<PaymentStatusPage />} />
              <Route path="terms" element={<RegulaminPage />} />
              <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="cookie-policy" element={<CookiePolicyPage />} />
              <Route path="withdrawal" element={<WithdrawalPage />} />
              <Route path="*" element={<NotFound />} />
            </>
          )}
        </Route>

        {/* Legacy URLs without lang prefix → redirect to /pl/... */}
        <Route path="*" element={<LegacyRedirect />} />
      </Routes>
    </Suspense>
  );
}

function ThemeApplier({ children }: { children: React.ReactNode }) {
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-dusk-gold", "theme-ivory-gold", "theme-frost-gold", "theme-rose-gold", "theme-ocean-gold", "theme-summer-mint", "theme-rainbow-pride", "theme-kawaii-cats");
    if (theme !== "dark-gold") {
      root.classList.add(`theme-${theme}`);
    }
  }, [theme]);
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <I18nProvider>
    <ThemeApplier>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
        <CookieConsentBanner />
        <TidioIdentify />
      </BrowserRouter>

    </TooltipProvider>
    </ThemeApplier>
    </I18nProvider>
  </QueryClientProvider>
);

export default App;
